/// <reference types="vite/client" />
/**
 * Audio Registry - Global registry for audio endpoint lookups
 *
 * Provides O(1) access to AudioEndpoints and their GainNodes/AnalyserNodes.
 * Used by audio nodes to connect internal processing to endpoint GainNodes.
 */

import type { AudioEndpoint } from './AudioEndpoint';
import type { IGainNode, IAudioContext } from 'standardized-audio-context';

// ─────────────────────────────────────────────────────────────
// Registry Storage
// ─────────────────────────────────────────────────────────────

/** Global registry of AudioEndpoints */
const audioEndpointRegistry: Map<string, AudioEndpoint> = new Map();

/**
 * Build registry key for an audio endpoint.
 */
export function makeAudioKey(instanceId: string, nodeId: string, endpointId: string): string {
  return `${instanceId}.${nodeId}.${endpointId}`;
}

// ─────────────────────────────────────────────────────────────
// Registration
// ─────────────────────────────────────────────────────────────

/**
 * Register an audio endpoint in the global registry.
 */
export function registerAudioEndpoint(
  instanceId: string,
  nodeId: string,
  endpointId: string,
  endpoint: AudioEndpoint
): void {
  audioEndpointRegistry.set(makeAudioKey(instanceId, nodeId, endpointId), endpoint);
}

/**
 * Unregister an audio endpoint from the global registry.
 */
export function unregisterAudioEndpoint(
  instanceId: string,
  nodeId: string,
  endpointId: string
): void {
  audioEndpointRegistry.delete(makeAudioKey(instanceId, nodeId, endpointId));
}

// ─────────────────────────────────────────────────────────────
// Lookups
// ─────────────────────────────────────────────────────────────

/**
 * Get an audio endpoint by path.
 */
export function getAudioEndpoint(
  instanceId: string,
  nodeId: string,
  endpointId: string
): AudioEndpoint | undefined {
  return audioEndpointRegistry.get(makeAudioKey(instanceId, nodeId, endpointId));
}

/**
 * Get the GainNode for an audio endpoint.
 * Returns undefined if endpoint not found.
 */
export function getGainNode(
  instanceId: string,
  nodeId: string,
  endpointId: string
): IGainNode<IAudioContext> | undefined {
  const endpoint = audioEndpointRegistry.get(makeAudioKey(instanceId, nodeId, endpointId));
  return endpoint?.gainNode;
}

/**
 * Get all registered audio endpoints (for batch queries).
 */
export function getAllAudioEndpoints(): Map<string, AudioEndpoint> {
  return audioEndpointRegistry;
}

/**
 * Clear all entries from the registry.
 */
export function clearAudioRegistry(): void {
  audioEndpointRegistry.clear();
}

// ─────────────────────────────────────────────────────────────
// HMR Cleanup
// ─────────────────────────────────────────────────────────────

// @ts-ignore - Vite HMR
if (import.meta.hot) {
  // @ts-ignore
  import.meta.hot.dispose(() => {
    clearAudioRegistry();
  });
}

/**
 * Get time-domain waveform data for a specific audio endpoint.
 * Returns null if endpoint not found.
 */
export function getTimeDomainData(
  instanceId: string,
  nodeId: string,
  endpointId: string,
): Float32Array | null {
  const endpoint = audioEndpointRegistry.get(makeAudioKey(instanceId, nodeId, endpointId));
  return endpoint?.getTimeDomainData() ?? null;
}

/**
 * Get frequency bins for a specific audio endpoint.
 * Returns null if endpoint not found.
 */
export function getFrequencyBins(
  instanceId: string,
  nodeId: string,
  endpointId: string,
  binCount = 16
): number[] | null {
  const endpoint = audioEndpointRegistry.get(makeAudioKey(instanceId, nodeId, endpointId));
  if (!endpoint) {
    return null;
  }
  return endpoint.getFrequencyBins(binCount);
}
